import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import { X, AlertCircle, ChevronDown, Search, RotateCw } from 'lucide-react';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const base = isLocal ? 'http://localhost:8080' : '';
  if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
  return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const UnclaimedUpdateTab = ({ toast }) => {
  const { hasPermission } = useAuth();

  // Search States
  const [unclaimedSearchInput, setUnclaimedSearchInput] = useState('');
  const [unclaimedSearchKeyword, setUnclaimedSearchKeyword] = useState('');

  // Pagination States
  const [unclaimedPage, setUnclaimedPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_unclaimed_update');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setUnclaimedPage = useCallback((valOrFn) => {
    setUnclaimedPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_unclaimed_update', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [unclaimedPageSize, setUnclaimedPageSize] = useState(25);
  const [unclaimedTotalPages, setUnclaimedTotalPages] = useState(0);
  const [unclaimedTotalItems, setUnclaimedTotalItems] = useState(0);

  // Data States
  const [unclaimedListings, setUnclaimedListings] = useState([]);
  const [unclaimedLoading, setUnclaimedLoading] = useState(false);

  // Modal & Form States
  const [deleteUnclaimedListing, setDeleteUnclaimedListing] = useState(null);
  const [editUnclaimedListing, setEditUnclaimedListing] = useState(null);
  const [unclaimedEditForm, setUnclaimedEditForm] = useState({
    companyName: '',
    aboutCompany: '',
    phone: '',
    email: '',
    address: '',
    area: '',
    state: '',
    city: '',
    zipcode: '',
    website: '',
    categoryId: '',
    tagIds: [],
    brandIds: [],
    gstin: '',
    establishedSince: '',
    instagram: '',
    linkedin: '',
    facebook: '',
    twitter: ''
  });

  const [unclaimedLogoFile, setUnclaimedLogoFile] = useState(null);
  const [unclaimedLogoPreview, setUnclaimedLogoPreview] = useState('');
  const [unclaimedBannerFile, setUnclaimedBannerFile] = useState(null);
  const [unclaimedBannerPreview, setUnclaimedBannerPreview] = useState('');
  const [unclaimedAvailableTags, setUnclaimedAvailableTags] = useState([]);
  const [unclaimedEditSubmitLoading, setUnclaimedEditSubmitLoading] = useState(false);

  // Category and Tag Lists for Form
  const [categories, setCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);

  const fetchUnclaimedListings = useCallback(async () => {
    if (!hasPermission('APPROVE_LISTINGS')) return;
    setUnclaimedLoading(true);
    try {
      const res = await vendorService.getAdminListings('', unclaimedSearchKeyword, unclaimedPage, unclaimedPageSize, null, false);
      if (res.success && res.data) {
        setUnclaimedListings(res.data.listings || []);
        setUnclaimedTotalPages(res.data.totalPages || 0);
        setUnclaimedTotalItems(res.data.totalItems || 0);
      } else {
        toast.error(res.error || "Failed to fetch unclaimed listings");
      }
    } catch (err) {
      console.error("Error fetching unclaimed listings:", err);
      toast.error("An error occurred while fetching unclaimed listings.");
    } finally {
      setUnclaimedLoading(false);
    }
  }, [hasPermission, unclaimedSearchKeyword, unclaimedPage, unclaimedPageSize, toast]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const catRes = await vendorService.getCategories(true);
      if (catRes.success) setCategories(catRes.data || []);
      const brandRes = await vendorService.getAllBrandsAdmin();
      if (brandRes.success) setAllBrands(brandRes.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchUnclaimedListings();
  }, [fetchUnclaimedListings]);

  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  const handleOpenEditUnclaimedListing = async (listing) => {
    setEditUnclaimedListing(listing);
    setUnclaimedLogoFile(null);
    setUnclaimedLogoPreview(listing.logoUrl ? getImageUrl(listing.logoUrl) : '');
    setUnclaimedBannerFile(null);
    setUnclaimedBannerPreview(listing.profileBannerUrl ? getImageUrl(listing.profileBannerUrl) : '');

    // Set form fields
    setUnclaimedEditForm({
      companyName: listing.companyName || '',
      aboutCompany: listing.aboutCompany || '',
      phone: listing.phone || '',
      email: listing.email || '',
      address: listing.address || '',
      area: listing.area || '',
      state: listing.state || '',
      city: listing.city || '',
      zipcode: listing.zipcode || '',
      website: listing.website || '',
      categoryId: listing.categoryId || '',
      tagIds: listing.tags ? listing.tags.map(t => t.id) : [],
      brandIds: listing.brands ? listing.brands.map(b => b.id) : [],
      gstin: listing.gstin || '',
      establishedSince: listing.establishedSince || '',
      instagram: listing.instagram || '',
      linkedin: listing.linkedin || '',
      facebook: listing.facebook || '',
      twitter: listing.twitter || ''
    });

    // Fetch tags for category
    if (listing.categoryId) {
      try {
        const res = await vendorService.getTagsByCategory(listing.categoryId);
        if (res.success && res.data) {
          setUnclaimedAvailableTags(res.data || []);
        }
      } catch (err) {
        console.error("Failed to load tags for category:", err);
      }
    } else {
      setUnclaimedAvailableTags([]);
    }
  };

  const handleUnclaimedCategoryChange = async (catId) => {
    setUnclaimedEditForm(prev => ({ ...prev, categoryId: catId, tagIds: [] }));
    setUnclaimedAvailableTags([]);
    if (catId) {
      try {
        const res = await vendorService.getTagsByCategory(catId);
        if (res.success && res.data) {
          setUnclaimedAvailableTags(res.data || []);
        }
      } catch (err) {
        console.error("Failed to load tags for category:", err);
      }
    }
  };

  const handleUnclaimedEditSubmit = async (e) => {
    e.preventDefault();
    setUnclaimedEditSubmitLoading(true);

    const form = unclaimedEditForm;
    const errors = [];
    if (!form.companyName || !form.companyName.trim()) errors.push("Company Name is required");
    if (!form.email || !form.email.trim()) errors.push("Email is required");

    if (errors.length > 0) {
      toast.warning(errors.join(", "));
      setUnclaimedEditSubmitLoading(false);
      return;
    }

    const formData = new FormData();
    const listingPayload = {
      companyName: form.companyName.trim(),
      aboutCompany: form.aboutCompany.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      area: form.area.trim(),
      state: form.state.trim(),
      city: form.city.trim(),
      zipcode: form.zipcode.trim(),
      website: form.website.trim() || null,
      categoryId: Number(form.categoryId),
      tagIds: form.tagIds.map(Number),
      brandIds: form.brandIds.map(Number),
      gstin: form.gstin.trim(),
      establishedSince: form.establishedSince ? Number(form.establishedSince) : null,
      instagram: form.instagram.trim() || null,
      linkedin: form.linkedin.trim() || null,
      facebook: form.facebook.trim() || null,
      twitter: form.twitter.trim() || null,
      businessHours: editUnclaimedListing.businessHours || { type: 'ALWAYS_OPEN' }
    };

    formData.append("listing", JSON.stringify(listingPayload));
    if (unclaimedLogoFile) {
      formData.append("logo", unclaimedLogoFile);
    }
    if (unclaimedBannerFile) {
      formData.append("profileBanner", unclaimedBannerFile);
    }

    try {
      const res = await vendorService.adminUpdateUnclaimedListing(editUnclaimedListing.id, formData);
      if (res.success) {
        toast.success("Unclaimed listing updated successfully.");
        setEditUnclaimedListing(null);
        fetchUnclaimedListings();
      } else {
        toast.error(res.error || "Failed to update unclaimed listing");
      }
    } catch (err) {
      toast.error("An error occurred while updating unclaimed listing.");
      console.error(err);
    } finally {
      setUnclaimedEditSubmitLoading(false);
    }
  };

  const handleDeleteUnclaimedListing = (id, companyName) => {
    setDeleteUnclaimedListing({ id, companyName });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6 space-y-6 text-left">
      <div className="flex justify-between items-center gap-3 select-none">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Unclaimed Listings Registry</h2>
          <p className="text-xs text-gray-400 mt-1 font-semibold">Search, select, and update details for imported unclaimed business listings.</p>
        </div>
        <span className="text-xs text-gray-500 font-bold uppercase tracking-wider bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl">
          Total unclaimed: {unclaimedTotalItems}
        </span>
      </div>

      {/* Search Control */}
      <div className="flex gap-3 items-center w-full select-none">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by company name, city, phone or state..."
            value={unclaimedSearchInput}
            onChange={(e) => setUnclaimedSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setUnclaimedSearchKeyword(unclaimedSearchInput);
                setUnclaimedPage(0);
              }
            }}
            className="w-full bg-white border border-gray-200 rounded-xl py-2.5 pl-10 pr-4 text-xs font-semibold focus:border-[#2ab391] focus:outline-none shadow-sm"
          />
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" />
        </div>
        <button
          type="button"
          onClick={() => {
            setUnclaimedSearchKeyword(unclaimedSearchInput);
            setUnclaimedPage(0);
          }}
          className="px-5 py-2.5 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap shadow-sm shadow-[#2ab391]/10"
        >
          Search
        </button>
      </div>

      {unclaimedLoading ? (
        <div className="flex flex-col justify-center items-center py-20 select-none">
          <div className="w-8 h-8 border-3 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-xs text-gray-500 mt-3 font-semibold">Fetching unclaimed listings...</span>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
                  <th className="p-4 pl-6">Listing Details</th>
                  <th className="p-4">Contact Phone</th>
                  <th className="p-4">Listing Email</th>
                  <th className="p-4">Location (City, State)</th>
                  <th className="p-4 pr-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
                {unclaimedListings.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-12 text-center text-gray-400 font-semibold bg-white select-none">
                      No unclaimed listings found matching your search.
                    </td>
                  </tr>
                ) : (
                  unclaimedListings.map((listing) => (
                    <tr key={listing.id} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                      <td className="p-4 pl-6 text-left">
                        <div className="font-semibold text-gray-800">{listing.companyName}</div>
                        <div className="text-[10px] text-gray-400 font-medium mt-0.5">ID: {listing.id}</div>
                      </td>
                      <td className="p-4 text-gray-700 text-left font-mono">{listing.phone || 'N/A'}</td>
                      <td className="p-4 text-gray-700 text-left break-all">{listing.email || 'N/A'}</td>
                      <td className="p-4 text-gray-700 text-left font-semibold text-[#2ab391]">
                        {listing.city ? `${listing.city}, ` : ''}{listing.state || 'N/A'}
                      </td>
                      <td className="p-4 pr-6 text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => handleOpenEditUnclaimedListing(listing)}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2ab391] hover:text-[#23977a] transition-colors bg-[#eaf7f4] border border-[#2ab391]/20 px-3.5 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                          >
                            Edit Details
                          </button>
                          <button
                            onClick={() => handleDeleteUnclaimedListing(listing.id, listing.companyName)}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 transition-colors bg-red-50 hover:bg-red-100/50 border border-red-200/50 px-3.5 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {unclaimedTotalPages > 0 && (
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-505 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
              <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
                Showing {unclaimedPage * unclaimedPageSize + 1} to {Math.min((unclaimedPage + 1) * unclaimedPageSize, unclaimedTotalItems)} of {unclaimedTotalItems} unclaimed listings
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
                <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => fetchUnclaimedListings()}
                    disabled={unclaimedLoading}
                    title="Reload current page"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RotateCw size={12} className={unclaimedLoading ? 'animate-spin' : ''} />
                    Reload
                  </button>
                  <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Show</span>
                  <div className="relative">
                    <select
                      value={unclaimedPageSize}
                      onChange={(e) => {
                        setUnclaimedPageSize(Number(e.target.value));
                        setUnclaimedPage(0);
                      }}
                      className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                      <ChevronDown size={12} />
                    </span>
                  </div>
                  <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Entries</span>
                </div>

                <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setUnclaimedPage(p => Math.max(0, p - 1))}
                    disabled={unclaimedPage === 0}
                    className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
                  >
                    Previous
                  </button>

                  <div className="hidden sm:flex items-center gap-1">
                    {getPageNumbers(unclaimedPage, unclaimedTotalPages).map((p, idx) => {
                      if (p === 'ellipsis1' || p === 'ellipsis2') {
                        return <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 font-bold">...</span>;
                      }
                      return (
                        <button
                          key={p}
                          onClick={() => setUnclaimedPage(p)}
                          className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all cursor-pointer ${
                            unclaimedPage === p
                              ? 'bg-[#2ab391] text-white shadow-md shadow-[#2ab391]/10'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {p + 1}
                        </button>
                      );
                    })}
                  </div>

                  <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                    {unclaimedPage + 1} / {unclaimedTotalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() => setUnclaimedPage(p => Math.min(unclaimedTotalPages - 1, p + 1))}
                    disabled={unclaimedPage >= unclaimedTotalPages - 1}
                    className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Unclaimed Listing Confirmation Modal */}
      {deleteUnclaimedListing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto p-4 flex justify-center items-center select-none">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 p-6 text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-red-50 text-red-600 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-gray-800">Delete Unclaimed Listing</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                Are you sure you want to delete the unclaimed listing <strong className="text-gray-700">"{deleteUnclaimedListing.companyName}"</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteUnclaimedListing(null)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const { id, companyName } = deleteUnclaimedListing;
                  setDeleteUnclaimedListing(null);
                  try {
                    const res = await vendorService.adminDeleteUnclaimedListing(id);
                    if (res.success) {
                      toast.success(`Unclaimed listing "${companyName}" deleted successfully.`);
                      fetchUnclaimedListings();
                    } else {
                      toast.error(res.error || "Failed to delete unclaimed listing");
                    }
                  } catch (err) {
                    toast.error("An error occurred while deleting the listing.");
                    console.error(err);
                  }
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-red-600/10 cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Unclaimed Listing Modal */}
      {editUnclaimedListing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto p-4 md:p-6 flex justify-center items-start">
          <div className="bg-white max-w-4xl w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 my-8 md:my-12 relative flex flex-col">

            {/* Header */}
            <div className="py-4 px-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 text-left select-none">
              <div>
                <span className="text-[10px] uppercase font-bold text-[#2ab391] tracking-wider">Update Details (Unclaimed Only)</span>
                <h3 className="text-lg font-bold text-gray-800 mt-1">Edit Unclaimed Listing</h3>
              </div>
              <button
                type="button"
                onClick={() => setEditUnclaimedListing(null)}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUnclaimedEditSubmit} className="flex flex-col overflow-hidden">
              {/* Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6 text-left">

                {/* Banner & Logo Media Section */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 select-none">Media & Branding</h4>

                  {/* Banner Area */}
                  <div className="relative h-44 bg-slate-100 rounded-xl overflow-hidden border border-gray-200 flex items-center justify-center group select-none">
                    {unclaimedBannerPreview ? (
                      <img
                        src={unclaimedBannerPreview}
                        alt="Banner Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 text-gray-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs font-semibold text-gray-500">No Profile Banner Uploaded</span>
                      </div>
                    )}
                    <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold cursor-pointer select-none">
                      <span>Change Banner Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUnclaimedBannerFile(file);
                            setUnclaimedBannerPreview(URL.createObjectURL(file));
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Logo Area */}
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex-shrink-0 flex items-center justify-center group select-none">
                      {unclaimedLogoPreview ? (
                        <img
                          src={unclaimedLogoPreview}
                          alt="Logo Preview"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span className="text-gray-400 font-bold text-lg select-none">Logo</span>
                      )}
                      <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold cursor-pointer select-none">
                        <span className="text-center px-1">Upload Logo</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setUnclaimedLogoFile(file);
                              setUnclaimedLogoPreview(URL.createObjectURL(file));
                            }
                          }}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div className="text-xs select-none">
                      <p className="font-bold text-gray-700">Company Logo & Branding</p>
                      <p className="text-gray-500 mt-0.5 leading-relaxed font-semibold">Upload high-resolution images. Formats: JPG, PNG. Max size: 2MB.</p>
                    </div>
                  </div>
                </div>

                {/* Section 1: Business Profile Info */}
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 select-none">Profile Information</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Company Name</label>
                      <input
                        type="text"
                        required
                        value={unclaimedEditForm.companyName}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, companyName: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Email Address</label>
                      <input
                        type="email"
                        required
                        value={unclaimedEditForm.email}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, email: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Contact Phone</label>
                      <input
                        type="text"
                        value={unclaimedEditForm.phone}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, phone: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Website URL</label>
                      <input
                        type="url"
                        value={unclaimedEditForm.website}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, website: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">GSTIN / Tax ID</label>
                      <input
                        type="text"
                        value={unclaimedEditForm.gstin}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, gstin: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Established Since (Year)</label>
                      <input
                        type="number"
                        placeholder="e.g. 2015"
                        value={unclaimedEditForm.establishedSince}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, establishedSince: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">About Company / Description</label>
                    <textarea
                      rows={4}
                      value={unclaimedEditForm.aboutCompany}
                      onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, aboutCompany: e.target.value })}
                      className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                    />
                  </div>
                </div>

                {/* Section 2: Address details */}
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 select-none">Address & Location</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Address Description</label>
                      <input
                        type="text"
                        value={unclaimedEditForm.address}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, address: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">State</label>
                      <input
                        type="text"
                        value={unclaimedEditForm.state}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, state: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">City</label>
                      <input
                        type="text"
                        value={unclaimedEditForm.city}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, city: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Area / Locality</label>
                      <input
                        type="text"
                        value={unclaimedEditForm.area}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, area: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Zip Code</label>
                      <input
                        type="text"
                        value={unclaimedEditForm.zipcode}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, zipcode: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Categories, tags & brands */}
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 select-none">Categorization & Filter Tags</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Directory Category</label>
                      <select
                        value={unclaimedEditForm.categoryId}
                        onChange={(e) => handleUnclaimedCategoryChange(e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-[#2ab391] cursor-pointer"
                      >
                        <option value="">Select Category...</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Brands Associated</label>
                      <div className="border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto space-y-1.5 bg-slate-50/50 select-none">
                        {allBrands.length === 0 ? (
                          <span className="text-xs text-gray-400 italic">No brands registry loaded</span>
                        ) : (
                          allBrands.map(brd => {
                            const isChecked = unclaimedEditForm.brandIds.includes(brd.id);
                            return (
                              <label key={brd.id} className="flex items-center gap-2 text-xs text-gray-700 font-semibold cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    if (isChecked) {
                                      setUnclaimedEditForm(prev => ({ ...prev, brandIds: prev.brandIds.filter(id => id !== brd.id) }));
                                    } else {
                                      setUnclaimedEditForm(prev => ({ ...prev, brandIds: [...prev.brandIds, brd.id] }));
                                    }
                                  }}
                                  className="rounded text-[#2ab391] focus:ring-[#2ab391]"
                                />
                                {brd.name}
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Search Tag Association</label>
                      {unclaimedEditForm.categoryId ? (
                        <div className="border border-gray-200 rounded-xl p-3 max-h-48 overflow-y-auto flex flex-wrap gap-2 bg-slate-50/50 select-none">
                          {unclaimedAvailableTags.length === 0 ? (
                            <span className="text-xs text-gray-400 italic">No tags defined for this category</span>
                          ) : (
                            unclaimedAvailableTags.map(tag => {
                              const isChecked = unclaimedEditForm.tagIds.includes(tag.id);
                              return (
                                <label
                                  key={tag.id}
                                  className={`px-3 py-1.5 rounded-lg border text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 select-none ${
                                    isChecked
                                      ? 'bg-teal-50 border-[#2ab391] text-teal-800'
                                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      if (isChecked) {
                                        setUnclaimedEditForm(prev => ({ ...prev, tagIds: prev.tagIds.filter(id => id !== tag.id) }));
                                      } else {
                                        setUnclaimedEditForm(prev => ({ ...prev, tagIds: [...prev.tagIds, tag.id] }));
                                      }
                                    }}
                                    className="hidden"
                                  />
                                  {tag.name}
                                </label>
                              );
                            })
                          )}
                        </div>
                      ) : (
                        <div className="border border-gray-200/60 rounded-xl p-4 text-center text-xs text-gray-400 italic bg-gray-50 select-none">
                          Please select a Category first to view available search tags.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 4: Social media handles */}
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 select-none">Social Network Handles</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Instagram URL</label>
                      <input
                        type="url"
                        value={unclaimedEditForm.instagram}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, instagram: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-805 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">LinkedIn URL</label>
                      <input
                        type="url"
                        value={unclaimedEditForm.linkedin}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, linkedin: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-805 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Facebook URL</label>
                      <input
                        type="url"
                        value={unclaimedEditForm.facebook}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, facebook: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-805 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5 select-none">Twitter URL</label>
                      <input
                        type="url"
                        value={unclaimedEditForm.twitter}
                        onChange={(e) => setUnclaimedEditForm({ ...unclaimedEditForm, twitter: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm font-semibold text-gray-805 focus:outline-none focus:border-[#2ab391] transition-all"
                      />
                    </div>
                  </div>
                </div>

              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => setEditUnclaimedListing(null)}
                  disabled={unclaimedEditSubmitLoading}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={unclaimedEditSubmitLoading}
                  className="px-5 py-2 bg-[#2ab391] hover:bg-[#23977a] disabled:bg-[#7ed4bf] disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  {unclaimedEditSubmitLoading ? "Saving Changes..." : "Save Changes"}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnclaimedUpdateTab;
